import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useUpdateEstablishmentCompany } from "../../../../hooks/useUpdateEstablishmentCompany";
import { updateEstablishmentCompanySchema, UpdateEstablishmentCompanyValidationSchema } from "../../../../schemas/updateEstablishmentCompany.schema";
import { updateEstablishmentCompanyDefaultValues } from "../../../../utils/updateEstablishmentCompanyDefaultValues";

interface UpdateEstablishmentCaModuleProps {
  establishmentCompany?: EstablishmentCompanyData;
  disabled?: boolean;
}

const UpdateEstablishmentCaModule = ({ establishmentCompany }: UpdateEstablishmentCaModuleProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [searchProductCategories, setSearchProductCategories] = useDebounce('');

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { mutate: updateEstablishmentCompany, isPending: updateEstablishmentCompanyIsPending } = useUpdateEstablishmentCompany({
    establishmentCompanyId: establishmentCompany?.id,
    establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false);
    },
    onError: (error) => {
      setIsOpen(false);
      setIsError(true);
      reset(updateEstablishmentCompanyDefaultValues(establishmentCompany))
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateEstablishmentCompanyValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanySchema),
    defaultValues: updateEstablishmentCompanyDefaultValues(establishmentCompany),
  });

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyValidationSchema> = (data) => updateEstablishmentCompany(data);

  const { data: productCategoriesOptions, isPending: productCategoriesIsPending } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  useEffect(() => {
    reset(updateEstablishmentCompanyDefaultValues(establishmentCompany));
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={
          <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Update">
            Editar
          </Button>
        }
        useCancellableFooter
        showClose={false}
        title="Editar dados para abastecimento"
        className="min-w-[720px]"
        bodyClassName="text-white-800"
        titleClass="text-2xl"
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        disabled={updateEstablishmentCompanyIsPending}
        confirmButtonIsLoading={updateEstablishmentCompanyIsPending}
        onCancel={reset}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form>
            <div className="flex flex-col gap-4">
              <SelectController
                control={control}
                name="product_categories"
                label="Serviços permitidos para empresa"
                isMulti
                onInputChange={setSearchProductCategories}
                options={productCategoriesOptions}
                isLoading={productCategoriesIsPending}
                isDisabled={productCategoriesIsPending}
                errorMessage={errors.product_categories?.message || undefined}
              />

              <Controller
                control={control}
                name="matrix_branch_attributes.payment_advance_daily_interest"
                render={(({ field }) => (
                  <UnitTextField
                    label="Taxa diária de juros de adiantamento"
                    decimals={2}
                    unit="%"
                    forceValue
                    {...field}
                    errorMessage={errors.matrix_branch_attributes?.payment_advance_daily_interest?.message || undefined}
                  />
                ))}
              />
            </div>
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError={isError}
      />
    </>
  );
};

export default UpdateEstablishmentCaModule;
